import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Stack, useTheme } from '@mui/material';
import {
  hash,
  emptyId,
  generateTestIds,
  useEditorStore,
  useFlowBuilder2Store,
  FlowBuilder2StepFieldNamesEnum,
  useFlowBuilder2MoveStepMutation,
  Events,
  useEvents,
} from '@diana/core';
import { NodeHeader } from './NodeHeader';
import { DropBoxBody } from './FlowExplorer.styles';
import { findLastIndex, isEqual, uniq, uniqBy } from 'lodash';
import { TaskNode } from './TaskNode';
import { StepNameField } from '../StepNameField/StepNameField';
import { useDeleteStep } from '../../Support/useDeleteStep';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { useAddTask } from '../../Support/useAddTask';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { ExpandButton } from './ExpandButton';
import { useExpandItem } from './useExpandItem';
import { scrollToStep } from '../../Support/useBuilderScroll';

export interface IStepNodeProps {
  stepId: string;
  stepIndex: number;
  level?: number;
  canDrag?: boolean;
}

interface IDragItem {
  stepId: string;
}

interface IDragCollected {
  isDragging: boolean;
}

interface IDropCollected {
  isOver: boolean;
}

const DraggableStep = Symbol('step');

export const StepNodeTestIds = generateTestIds('StepNode', ['Root', 'ToggleCollapseButton']);

export const StepNode = (props: IStepNodeProps) => {
  const { stepId, stepIndex, level = 0, canDrag } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const bodyRef = useRef<HTMLDivElement>(null);

  const [dropPlacement, setDropPlacement] = useState<'before' | 'after' | null>(null);

  const { palette, typography, spacing } = useTheme();

  const activeTaskId = useFlowBuilder2PageStore((s) => s.activeTaskId);
  const setActiveTaskId = useFlowBuilder2PageStore((s) => s.setActiveTaskId);

  const getFlow = useEditorStore((s) => s.getFlow);
  const updateFlow = useEditorStore((s) => s.updateFlow);
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);
  const validationResult = useFlowBuilder2Store((s) => {
    return s.getFieldState(stepId, 'step', FlowBuilder2StepFieldNamesEnum.DisplayName)
      .validationResult;
  }, isEqual);
  const canDelete = useEditorStore((s) => uniqBy(s.flow?.steps, (step) => step.stepId).length > 1);
  const displayName = useEditorStore((s) => {
    return s.flow?.steps.find((step) => step.stepId === stepId)?.stepDisplayName ?? '';
  });
  const taskIds = useEditorStore((s) => {
    const steps = s.flow?.steps.filter((step) => step.stepId === stepId) ?? [];
    return uniq(steps.map((step) => step.taskId));
  }, isEqual);
  const isSelected = useEditorStore((s) => {
    return (
      s.flow?.steps.some((step) => step.stepId === stepId && step.taskId === activeTaskId) ?? false
    );
  });

  const { handleExpand, isExpanded } = useExpandItem({ itemId: stepId, level });
  const { deleteStep, isDeletingStep } = useDeleteStep();
  const { addTask } = useAddTask();

  const onAddItem = async () => {
    await addTask(stepId);
  };

  const onClick = () => {
    handleExpand(true);
    scrollToStep(stepId);
    if (taskIds[0]) setActiveTaskId(taskIds[0]);
  };

  const { mutateAsync: moveStep } = useFlowBuilder2MoveStepMutation({
    onSuccess: (flow) => {
      updateFlow((draft) => ({ ...draft, steps: flow.steps }));
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<IDragItem, unknown, IDragCollected>(
    {
      type: DraggableStep,
      item: { stepId },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [stepId],
  );

  const [{ isOver }, drop] = useDrop<IDragItem, unknown, IDropCollected>(
    {
      accept: DraggableStep,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
      hover: (item, monitor) => {
        if (bodyRef.current) {
          const bodyRect = bodyRef.current.getBoundingClientRect();
          const pointerOffset = monitor.getClientOffset();
          if (item.stepId === stepId) setDropPlacement(null);
          else if (bodyRect && pointerOffset) {
            const center = bodyRect.top + bodyRect.height / 2;
            if (pointerOffset.y <= center) setDropPlacement('before');
            else setDropPlacement('after');
          }
        }
      },
      drop: (item) => {
        const flow = getFlow();
        const steps = uniqBy(flow?.steps ?? [], (s) => s.stepId);
        const targetIndex = steps.findIndex((s) => s.stepId === stepId);

        switch (dropPlacement) {
          case 'before':
            if (steps[targetIndex - 1]?.stepId === item.stepId) return;
            break;
          case 'after':
            if (steps[targetIndex + 1]?.stepId === item.stepId) return;
            break;
        }

        if (flow && targetIndex >= 0 && dropPlacement) {
          const priorStep = steps[dropPlacement === 'before' ? targetIndex - 1 : targetIndex];

          updateFlow((draft) => {
            const subjects = draft.steps.filter((s) => s.stepId === item.stepId);
            const newSteps = draft.steps.filter((s) => s.stepId !== item.stepId);

            const insertAfterIndex = findLastIndex(newSteps, (s) => s.id === priorStep?.id);
            const spliceIndex = insertAfterIndex < 0 ? 0 : insertAfterIndex + 1;

            newSteps.splice(spliceIndex, 0, ...subjects);
            draft.steps = newSteps;

            return draft;
          });

          registerRequest(
            () =>
              moveStep({
                flowId: flow.id,
                stepId: item.stepId,
                afterStepId: priorStep?.stepId ?? emptyId,
              }).catch(),
            {
              key: hash(['MOVE_STEP', item.stepId]),
              objectId: item.stepId,
              objectType: 'step',
            },
          );
        }
      },
    },
    [stepId, getFlow, updateFlow, registerRequest, moveStep, dropPlacement],
  );

  useEvents((ctx) => {
    Events.ToggleFlowBuilderNavStepNode.attach(ctx, ({ value }) => handleExpand(value));
  }, []);

  drop(bodyRef);
  preview(bodyRef);

  return (
    <DropBoxBody
      ref={bodyRef}
      data-testid={StepNodeTestIds.Root}
      data-dragging={isDragging}
      data-step-node-id={stepId}
      data-drop-placement={isOver ? dropPlacement : ''}
      position="relative"
    >
      <Box borderRadius={1} bgcolor={isDragging ? palette.action.hover : ''}>
        <Box ref={!!canDrag ? drag : undefined} sx={{ opacity: isDragging ? 0 : 1 }}>
          <NodeHeader
            canRename
            label={displayName}
            labelVariant="h5"
            untitledLabel={t('labels.untitledStep')}
            addItemTooltip={t('buttons.addTask')}
            isSelected={isSelected}
            onAddItem={onAddItem}
            actions={[
              {
                title: t('buttons.delete'),
                color: palette.error.main,
                disabled: !canDelete,
                loading: isDeletingStep,
                execute: async () => {
                  deleteStep({ stepId }).catch();
                },
              },
            ]}
            validationResult={validationResult}
            renderNameTextField={(props) => (
              <StepNameField
                stepId={stepId}
                multiline
                fullWidth
                variant="standard"
                disableValidationResultTooltip
                InputProps={{ sx: { p: 0 } }}
                inputProps={{
                  sx: {
                    py: 0,
                    px: 0.5,
                    borderRadius: 1.5,
                    backgroundColor: palette.background.card,
                  },
                }}
                sx={{
                  top: 4,
                  minHeight: spacing(3.5),
                  '.MuiInputBase-input, .MuiInputBase-input::placeholder': {
                    fontWeight: typography.fontWeightMedium,
                  },
                }}
                helperText={false}
                {...props}
              />
            )}
            highlightOnHover
            renderStartContent={() => (
              <ExpandButton
                isExpanded={isExpanded}
                onToggle={() => handleExpand(!isExpanded)}
                dataTestId={StepNodeTestIds.ToggleCollapseButton}
              />
            )}
            onClick={onClick}
          />

          <Collapse in={isExpanded} unmountOnExit>
            <Stack ml={3} pt={taskIds.length > 0 ? 0.25 : 0} gap={0.25}>
              {taskIds.map((taskId, taskIndex) => (
                <TaskNode
                  key={taskId}
                  taskId={taskId}
                  taskIndex={taskIndex}
                  stepId={stepId}
                  stepIndex={stepIndex}
                  isSingleTask={taskIds.length === 1}
                  level={level + 1}
                  canDrag
                />
              ))}
            </Stack>
          </Collapse>
        </Box>
      </Box>
    </DropBoxBody>
  );
};
